import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Plus,
  Edit,
  Trash2,
  Loader2,
  ArrowLeft,
  Code2,
  Variable,
  Database,
  FileText,
  Copy,
  CheckCircle,
  XCircle,
  Info,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MergeFieldSettingsPage = () => {
  const { user } = useAuth();
  const [customFields, setCustomFields] = useState([]);
  const [dataSources, setDataSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [deletingField, setDeletingField] = useState(null);
  const [saving, setSaving] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    field_type: 'static',
    static_value: '',
    data_source: '',
    mapping_field: '',
    is_active: true,
  });

  const allowedRoles = ['system_admin', 'executive', 'operations_manager'];
  const canManage = allowedRoles.includes(user?.role);

  useEffect(() => {
    if (canManage) {
      fetchCustomFields();
      fetchDataSources();
    }
  }, [canManage]);

  const fetchCustomFields = async () => {
    try {
      const response = await axios.get(`${API}/admin/custom-merge-fields`);
      setCustomFields(response.data || []);
    } catch (error) {
      console.error('Failed to load custom merge fields:', error);
      toast.error('Failed to load custom merge fields');
    } finally {
      setLoading(false);
    }
  };

  const fetchDataSources = async () => {
    try {
      const response = await axios.get(`${API}/admin/custom-merge-fields/data-sources`);
      setDataSources(response.data?.data_sources || []);
    } catch (error) {
      console.error('Failed to load data sources:', error);
    }
  };

  const handleOpenDialog = (field = null) => {
    if (field) {
      setEditingField(field);
      setFormData({
        name: field.name,
        display_name: field.display_name,
        description: field.description || '',
        field_type: field.field_type,
        static_value: field.static_value || '',
        data_source: field.data_source || '',
        mapping_field: field.mapping_field || '',
        is_active: field.is_active,
      });
    } else {
      setEditingField(null);
      setFormData({
        name: '',
        display_name: '',
        description: '',
        field_type: 'static',
        static_value: '',
        data_source: '',
        mapping_field: '',
        is_active: true,
      });
    }
    setShowDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingField) {
        await axios.put(`${API}/admin/custom-merge-fields/${editingField.id}`, formData);
        toast.success('Custom merge field updated successfully');
      } else {
        await axios.post(`${API}/admin/custom-merge-fields`, formData);
        toast.success('Custom merge field created successfully');
      }
      setShowDialog(false);
      fetchCustomFields();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save custom merge field';
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingField) return;
    setSaving(true);

    try {
      await axios.delete(`${API}/admin/custom-merge-fields/${deletingField.id}`);
      toast.success('Custom merge field deleted successfully');
      setShowDeleteDialog(false);
      setDeletingField(null);
      fetchCustomFields();
    } catch (error) {
      toast.error('Failed to delete custom merge field');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (fieldName) => {
    const fieldText = `{{custom_${fieldName}}}`;
    navigator.clipboard.writeText(fieldText);
    setCopiedField(fieldName);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  const getSelectedDataSource = () => {
    return dataSources.find((ds) => ds.id === formData.data_source);
  };

  if (!canManage) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="merge-field-settings-unauthorized">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <XCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Access Denied</h3>
            <p className="text-muted-foreground">
              You do not have permission to manage custom merge fields. This feature is
              available to System Admins, Executives, and Operations Managers.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="merge-field-settings-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="merge-field-settings-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/settings">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Settings
            </Link>
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Variable className="w-6 h-6" />
              Custom Merge Fields
            </h1>
            <p className="text-muted-foreground">
              Create and manage custom merge fields for message templates
            </p>
          </div>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="create-merge-field-btn">
          <Plus className="w-4 h-4 mr-2" />
          Create Merge Field
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-blue-900 mb-1">About Custom Merge Fields</p>
              <p className="text-blue-800">
                Custom merge fields allow you to add personalized data to message templates.
                <strong> Static fields</strong> use a fixed value you define, while
                <strong> Dynamic fields</strong> pull live data from participant, case, enrollment,
                or staff records. Use the format <code className="bg-blue-100 px-1 rounded">{'{{custom_fieldname}}'}</code> in templates.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Standard Fields Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4" />
            Standard Merge Fields
          </CardTitle>
          <CardDescription>
            These fields are built-in and always available. The new <code className="bg-muted px-1 rounded">{'{{staff_position}}'}</code> field
            resolves to the current user's job title/position.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 text-sm">
            {[
              '{{participant_first_name}}',
              '{{participant_full_name}}',
              '{{staff_name}}',
              '{{staff_email}}',
              '{{staff_position}}',
              '{{case_manager_name}}',
              '{{current_date}}',
              '{{organization_name}}',
            ].map((field) => (
              <code
                key={field}
                className="bg-muted px-2 py-1 rounded text-xs cursor-pointer hover:bg-muted/80"
                onClick={() => {
                  navigator.clipboard.writeText(field);
                  toast.success('Copied!');
                }}
              >
                {field}
              </code>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Custom Fields Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Code2 className="w-5 h-5" />
            Your Custom Merge Fields
          </CardTitle>
          <CardDescription>
            {customFields.length === 0
              ? 'No custom merge fields created yet'
              : `${customFields.length} custom field${customFields.length !== 1 ? 's' : ''}`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {customFields.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Variable className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>No custom merge fields yet.</p>
              <p className="text-sm">Click "Create Merge Field" to add one.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Field Name</TableHead>
                  <TableHead>Display Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Value / Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customFields.map((field) => (
                  <TableRow key={field.id} data-testid={`merge-field-row-${field.name}`}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="bg-muted px-2 py-1 rounded text-xs font-mono">
                          {'{{custom_' + field.name + '}}'}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => copyToClipboard(field.name)}
                          data-testid={`copy-field-${field.name}`}
                        >
                          {copiedField === field.name ? (
                            <CheckCircle className="w-3 h-3 text-green-500" />
                          ) : (
                            <Copy className="w-3 h-3" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{field.display_name}</TableCell>
                    <TableCell>
                      <Badge variant={field.field_type === 'static' ? 'secondary' : 'outline'}>
                        {field.field_type === 'static' ? (
                          <><FileText className="w-3 h-3 mr-1" /> Static</>
                        ) : (
                          <><Database className="w-3 h-3 mr-1" /> Dynamic</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs truncate text-sm text-muted-foreground">
                      {field.field_type === 'static'
                        ? field.static_value
                        : `${field.data_source}.${field.mapping_field}`}
                    </TableCell>
                    <TableCell>
                      {field.is_active ? (
                        <Badge variant="success" className="bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" /> Active
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <XCircle className="w-3 h-3 mr-1" /> Inactive
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => handleOpenDialog(field)}
                          data-testid={`edit-field-${field.name}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-600"
                          onClick={() => {
                            setDeletingField(field);
                            setShowDeleteDialog(true);
                          }}
                          data-testid={`delete-field-${field.name}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg" data-testid="merge-field-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingField ? 'Edit Custom Merge Field' : 'Create Custom Merge Field'}
            </DialogTitle>
            <DialogDescription>
              {editingField
                ? 'Update the merge field settings below.'
                : 'Define a new custom merge field for use in templates.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Field Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., company_tagline"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''),
                    })
                  }
                  disabled={!!editingField}
                  data-testid="merge-field-name-input"
                />
                <p className="text-xs text-muted-foreground">
                  Letters, numbers, underscores only. Cannot be changed after creation.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="display_name">Display Name *</Label>
                <Input
                  id="display_name"
                  placeholder="e.g., Company Tagline"
                  value={formData.display_name}
                  onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
                  data-testid="merge-field-display-name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe what this field is for..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                data-testid="merge-field-description-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="field_type">Field Type *</Label>
              <Select
                value={formData.field_type}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    field_type: value,
                    static_value: '',
                    data_source: '',
                    mapping_field: '',
                  })
                }
              >
                <SelectTrigger data-testid="merge-field-type-select">
                  <SelectValue placeholder="Select field type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="static">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Static - Fixed value
                    </div>
                  </SelectItem>
                  <SelectItem value="dynamic">
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4" />
                      Dynamic - From database
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.field_type === 'static' && (
              <div className="space-y-2">
                <Label htmlFor="static_value">Static Value *</Label>
                <Textarea
                  id="static_value"
                  placeholder="Enter the fixed value for this field..."
                  value={formData.static_value}
                  onChange={(e) => setFormData({ ...formData, static_value: e.target.value })}
                  rows={3}
                  data-testid="merge-field-static-value-input"
                />
              </div>
            )}

            {formData.field_type === 'dynamic' && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="data_source">Data Source *</Label>
                  <Select
                    value={formData.data_source}
                    onValueChange={(value) =>
                      setFormData({ ...formData, data_source: value, mapping_field: '' })
                    }
                  >
                    <SelectTrigger data-testid="merge-field-data-source-select">
                      <SelectValue placeholder="Select data source" />
                    </SelectTrigger>
                    <SelectContent>
                      {dataSources.map((ds) => (
                        <SelectItem key={ds.id} value={ds.id}>
                          <div>
                            <span className="font-medium">{ds.name}</span>
                            <span className="text-muted-foreground text-xs ml-2">
                              - {ds.description}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mapping_field">Mapping Field *</Label>
                  <Select
                    value={formData.mapping_field}
                    onValueChange={(value) => setFormData({ ...formData, mapping_field: value })}
                    disabled={!formData.data_source}
                  >
                    <SelectTrigger data-testid="merge-field-mapping-field-select">
                      <SelectValue placeholder="Select field to map" />
                    </SelectTrigger>
                    <SelectContent>
                      {getSelectedDataSource()?.common_fields.map((f) => (
                        <SelectItem key={f.field} value={f.field}>
                          <div>
                            <code className="text-xs bg-muted px-1 rounded mr-2">{f.field}</code>
                            <span className="text-muted-foreground text-xs">{f.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    You can also type a custom field path (e.g., nested.field) for advanced use.
                  </p>
                  <Input
                    placeholder="Or enter custom field path..."
                    value={formData.mapping_field}
                    onChange={(e) => setFormData({ ...formData, mapping_field: e.target.value })}
                    className="mt-2"
                    data-testid="merge-field-custom-mapping-input"
                  />
                </div>
              </>
            )}

            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center space-x-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  data-testid="merge-field-active-switch"
                />
                <Label htmlFor="is_active">Active</Label>
              </div>

              {formData.name && (
                <div className="text-sm text-muted-foreground">
                  Preview: <code className="bg-muted px-1 rounded">{'{{custom_' + formData.name + '}}'}</code>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="merge-field-save-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingField ? 'Update Field' : 'Create Field'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent data-testid="delete-merge-field-dialog">
          <DialogHeader>
            <DialogTitle>Delete Custom Merge Field</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the field{' '}
              <code className="bg-muted px-1 rounded">{'{{custom_' + (deletingField?.name || '') + '}}'}</code>?
              <br />
              <br />
              <strong>Warning:</strong> Any templates using this field will render it as blank.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={saving}
              data-testid="confirm-delete-merge-field-btn"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete Field
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MergeFieldSettingsPage;
